/**
 * Tested in the ProgramCard component tests
 */
import { BannerCard } from '@/components/banner-card';
import { BannerCardV3 } from '@/components/card/banner-card-v3';
import { CardVariant } from '@/components/card/types';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { EnumButtonName } from '@calm/evexia-analytics';
import { DS_HEIGHT, DS_WIDTH } from '@calm/evexia-design-tokens';
import Card from '@rhg/components/card';
import { useAudioTiming } from '@rhg/hooks/use-audio-timing';
import { ProgramContextProps } from '@rhg/hooks/use-program-context/types';
import { useTheme } from '@rhg/libs/material';
import { sxAppend } from '@rhg/libs/material/sx';
import { ProgramCardProps } from '..';
import { BannerActions } from '../banner-actions';
import { useVariantAgnosticProgramCardProps } from './use-variant-agnostic-program-card-props';
import { useVariantAwareProgramCardProps } from './use-variant-aware-program-card-props';

const styleHorizontalCard = {
    width: { md: `${DS_WIDTH.FORM_MAX_WIDTH}px`, sm: '100%' },
};

export type ProgramContextAwareProgramCardProps = Omit<
    ProgramCardProps,
    keyof ProgramContextProps
>;

interface ProgramCardViewProps {
    ownProps: ProgramContextAwareProgramCardProps;
    agnosticProps: ReturnType<typeof useVariantAgnosticProgramCardProps>;
    awareProps: ReturnType<typeof useVariantAwareProgramCardProps>;
    isMobile: boolean;
    /** Progress ring value — live for the active card, persisted otherwise. */
    progressValue: number | undefined;
}

/**
 * Presentational program card. Fully prop-driven and audio-agnostic: the
 * progress value is passed in, so it never subscribes to the audio timing.
 */
function ProgramCardView({
    ownProps,
    agnosticProps,
    awareProps,
    isMobile,
    progressValue,
}: ProgramCardViewProps) {
    const theme = useTheme();
    const {
        variant,
        titleComponent,
        additionalAction,
        joinAction,
        sx,
        contentSx,
        imageForcedSize,
        isNotClickable,
        imageWidth,
        priority,
        trackButton = EnumButtonName.ProgramCardViewProgram,
        trackProps,
        entireCardButtonProps,
        role,
        'aria-label': ariaLabel,
    } = ownProps;
    const {
        program,
        playHandler,
        updateViewedStatus,
        cardClickHandler,
        detailsRoute,
        label,
        categoryTitleText,
        categoryTitleColor,
        categoryIcon,
        subtitle,
        progressText,
        progressTextAlt,
        inProgressDTC,
        isPlaying,
        inProgressClinical,
        imageSrc,
        durationText,
        cardRef,
    } = agnosticProps;
    const {
        isEntireCardClickable,
        isImageSideRadiusEnabled,
        defaultContentSx,
        titleMaxLines,
        focusIcon,
        titleIcon,
        categoryDescription,
        contentTag,
        imageDecorations,
    } = awareProps;

    // TODO: Expecting this to be deprecated should remove with the feature flag in EVE-10710
    if (variant === CardVariant.BannerV2) {
        return (
            <BannerCard
                ref={cardRef}
                title={{
                    text: program?.title ?? '',
                    maxLines: titleMaxLines,
                    component: titleComponent,
                    icon: titleIcon,
                }}
                subtitle={{ text: subtitle ?? '' }}
                content={{
                    tag: contentTag,
                    sx: sxAppend(defaultContentSx, contentSx),
                }}
                category={{
                    title: categoryTitleText,
                    titleColor: categoryTitleColor,
                    icon: categoryIcon,
                    description: categoryDescription,
                }}
                progress={{
                    value: progressValue,
                    text: progressText(false),
                }}
                button={{
                    group: (
                        <BannerActions
                            program={program}
                            joinAction={joinAction}
                            variant={variant}
                            playHandler={playHandler}
                            durationText={durationText}
                            detailsRoute={detailsRoute}
                            isPlaying={isPlaying}
                            trackProps={trackProps}
                        />
                    ),
                    topSpacing: inProgressClinical
                        ? theme.ds.spacingUnits.CONTENT
                        : undefined,
                    additionalAction,
                }}
                onClick={cardClickHandler}
                sx={sx}
                isEntireCardClickable={isEntireCardClickable}
                variant={CardVariant.BannerV2}
                entireCardButtonProps={{
                    'aria-label': program?.title ?? '',
                    'aria-hidden': isEntireCardClickable ? true : undefined,
                    tabIndex: isEntireCardClickable ? 0 : -1,
                    'data-cy':
                        inProgressClinical || inProgressDTC
                            ? 'ProgramPlay'
                            : 'ProgramViewDetails',
                    ...entireCardButtonProps,
                }}
                label={label}
                aria-label={ariaLabel}
                data-syntheticid="ProgramCard"
                role={role}
                trackButton={trackButton}
                trackProps={trackProps}
                image={{
                    src: imageSrc,
                    decorations: imageDecorations,
                    width: imageWidth,
                    focusIcon: focusIcon,
                    forcedSize: imageForcedSize
                        ? imageForcedSize
                        : DS_HEIGHT.HORIZONTAL_CARD_SIZE,
                    sideRadiusEnabled: isImageSideRadiusEnabled,
                    props: { priority },
                }}
            />
        );
    }

    if (variant === CardVariant.BannerV3) {
        return (
            <BannerCardV3
                ref={cardRef}
                title={{
                    text: program?.title ?? '',
                    maxLines: titleMaxLines,
                    component: titleComponent,
                    icon: titleIcon,
                }}
                subtitle={{ text: subtitle ?? '' }}
                content={{
                    tag: contentTag,
                    sx: sxAppend(defaultContentSx, contentSx),
                }}
                category={{
                    title: categoryTitleText,
                    titleColor: categoryTitleColor,
                    icon: categoryIcon,
                    description: categoryDescription,
                }}
                progress={{
                    value: progressValue,
                    text: progressText(false),
                }}
                button={{
                    group: (
                        <BannerActions
                            program={program}
                            joinAction={joinAction}
                            variant={variant}
                            playHandler={playHandler}
                            durationText={durationText}
                            detailsRoute={detailsRoute}
                            isPlaying={isPlaying}
                            trackProps={trackProps}
                        />
                    ),
                    topSpacing: inProgressClinical
                        ? theme.ds.spacingUnits.CONTENT
                        : undefined,
                    additionalAction: isMobile ? undefined : additionalAction,
                }}
                container={{
                    onClick: cardClickHandler,
                    isClickable: isEntireCardClickable,
                    role,
                    trackButton,
                    trackProps,
                    'aria-hidden': isEntireCardClickable ? true : undefined,
                    'aria-label': label,
                    'data-syntheticid': 'ProgramCard',
                    'data-cy':
                        inProgressClinical || inProgressDTC
                            ? 'ProgramPlay'
                            : 'ProgramViewDetails',
                }}
                sx={sx}
                image={{
                    src: imageSrc,
                    decorations: imageDecorations,
                    width: imageWidth,
                    focusIcon: focusIcon,
                    forcedSize: imageForcedSize,
                    sideRadiusEnabled: isImageSideRadiusEnabled,
                    props: { priority },
                }}
            />
        );
    }

    return (
        <Card
            ref={cardRef}
            variant={variant}
            title={{
                text: program?.title ?? '',
                maxLines: titleMaxLines,
                component: titleComponent,
                icon: titleIcon,
            }}
            label={label}
            subtitle={{ text: subtitle ?? '' }}
            content={{
                tag: contentTag,
                sx: sxAppend(defaultContentSx, contentSx),
            }}
            category={{
                title: categoryTitleText,
                titleColor: categoryTitleColor,
                icon: categoryIcon,
                description: categoryDescription,
            }}
            progress={{
                value: progressValue,
                text: progressText(false),
                textAlt: progressTextAlt,
            }}
            button={{
                group: variant === 'banner' && (
                    <BannerActions
                        program={program}
                        joinAction={joinAction}
                        variant={variant}
                        playHandler={playHandler}
                        durationText={durationText}
                        detailsRoute={detailsRoute}
                        updateViewedStatus={updateViewedStatus}
                        trackProps={trackProps}
                    />
                ),
                topSpacing: inProgressClinical ? 6 : undefined,
                additionalAction,
            }}
            isNotClickable={isNotClickable}
            onClick={cardClickHandler}
            sx={sxAppend(
                variant === 'horizontal' ? styleHorizontalCard : undefined,
                sx
            )}
            isEntireCardClickable={isEntireCardClickable}
            entireCardButtonProps={{
                ...entireCardButtonProps,
                'data-cy':
                    variant === 'banner'
                        ? undefined
                        : inProgressClinical || inProgressDTC
                        ? 'ProgramPlay'
                        : 'ProgramViewDetails',
                'aria-hidden': variant === 'banner' ? true : undefined,
                tabIndex: variant === 'banner' ? -1 : 0,
            }}
            data-syntheticid="ProgramCard"
            role={role}
            aria-label={ariaLabel}
            trackButton={trackButton}
            trackProps={trackProps}
            image={{
                src: imageSrc,
                decorations: imageDecorations,
                width: imageWidth,
                focusIcon: focusIcon,
                forcedSize:
                    imageForcedSize || variant !== 'horizontal'
                        ? imageForcedSize
                        : DS_HEIGHT.HORIZONTAL_CARD_SIZE,
                sideRadiusEnabled: isImageSideRadiusEnabled,
                props: { priority },
            }}
        />
    );
}

/**
 * Active-card variant: subscribes to the ~4Hz audio timing so ONLY the one
 * active now-playing banner card animates its progress ring live. Mounted only
 * when the card is the active banner (see the container below), so exactly one
 * card subscribes at a time while every other card stays on the low-frequency
 * audio state and does not re-render on `timeupdate`. EVE-12668.
 */
function ActiveProgramCardView(
    props: Omit<ProgramCardViewProps, 'progressValue'>
) {
    const { timingState } = useAudioTiming();
    return (
        <ProgramCardView
            {...props}
            progressValue={timingState.currentPercent}
        />
    );
}

export function ProgramContextAwareProgramCard(
    ownProps: ProgramContextAwareProgramCardProps
) {
    const isMobile = useIsMobile();
    const agnosticProps = useVariantAgnosticProgramCardProps({
        isTopRecommendation: ownProps.isTopRecommendation ?? false,
        contentType: ownProps.contentType,
        isNotClickable: ownProps.isNotClickable,
        displayGuideProgress: ownProps.displayGuideProgress,
        trackProps: ownProps.trackProps,
        shouldShowSessionLength: ownProps.shouldShowSessionLength ?? false,
        playCurrentSessionOnClick: ownProps.playCurrentSessionOnClick,
    });
    const awareProps = useVariantAwareProgramCardProps({
        variant: ownProps.variant,
        isTopRecommendation: ownProps.isTopRecommendation ?? false,
        isCarouselCategoryDescriptionDisplayed:
            ownProps.isCarouselCategoryDescriptionDisplayed,
        carouselInProgressTitleMaxLines:
            ownProps.carouselInProgressTitleMaxLines ?? 1,
        isContributorAvatarDisplayedOnCarousel:
            ownProps.isContributorAvatarDisplayedOnCarousel,
        contentType: ownProps.contentType,
        additionalAction: ownProps.additionalAction,
        hideImageOverlays: ownProps.hideImageOverlays,
        isNotClickable: ownProps.isNotClickable,
        displayGuideProgress: ownProps.displayGuideProgress,
        imageIconTop: ownProps.imageIconTop,
        imageSideRadiusEnabled: ownProps.imageSideRadiusEnabled,
        trackProps: ownProps.trackProps,
        shouldShowSessionLength: ownProps.shouldShowSessionLength ?? false,
        contentTagOverride: ownProps.contentTagOverride,
        showNarratorAvatar: ownProps.showNarratorAvatar,
    });

    const isBannerVariant =
        ownProps.variant === CardVariant.BannerV2 ||
        ownProps.variant === CardVariant.BannerV3;
    const viewProps = { ownProps, agnosticProps, awareProps, isMobile };

    // Only the active banner card subscribes to the 4Hz timing (via
    // ActiveProgramCardView) to animate its progress ring live; every other
    // card renders once with persisted progress and never subscribes. Matches
    // the pre-EVE-12668 behavior where only banner variants showed live
    // progress. EVE-12668.
    return agnosticProps.isActiveAudio &&
        isBannerVariant &&
        agnosticProps.isClinicalProgram ? (
        <ActiveProgramCardView {...viewProps} />
    ) : (
        <ProgramCardView
            {...viewProps}
            progressValue={agnosticProps.progress()}
        />
    );
}
